import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/LoadingSpinner';
import trackingAnalyticsService from '../../services/trackingAnalyticsService';
import contentMonitoringService from '../../services/contentMonitoringService';
import embeddedAnalyticsService from '../../services/embeddedAnalyticsService';
import SocialMediaAnalyticsDashboard from '../../components/analytics/SocialMediaAnalyticsDashboard';
import analyticsFormulasService from '../../services/analyticsFormulasService';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';
import { GitCompare, Lightbulb } from 'lucide-react';

const formatNumber = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return (value || 0).toString();
};

const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return `₱${num.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const Analytics = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaignStats, setCampaignStats] = useState([]);
  const [overallStats, setOverallStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalInfluencers: 0,
    totalReach: 0,
    averageEngagement: 0,
    totalSpent: 0
  });
  const [trackingAnalytics, setTrackingAnalytics] = useState({
    totalClicks: 0,
    totalTrackingLinks: 0,
    clicksByPlatform: {},
    topPerformingLinks: [],
    campaignPerformance: []
  });
  const [contentMonitoringData, setContentMonitoringData] = useState({
    totalDetections: 0,
    totalReach: 0,
    totalEngagement: 0,
    platformBreakdown: {},
    detections: []
  });
  const [embeddedAnalyticsData, setEmbeddedAnalyticsData] = useState({
    totalEvents: 0,
    pageViews: 0,
    engagedViews: 0,
    deepEngagement: 0,
    qualityViews: 0,
    averageTimeOnPage: 0,
    eventsByType: {},
    eventsByInfluencer: {}
  });
  const [activeTab, setActiveTab] = useState('social');
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [compareSelectedIds, setCompareSelectedIds] = useState([]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        // Fetch campaigns for this brand
        const campaignsQuery = query(
          collection(db, 'campaigns'),
          where('brandId', '==', currentUser.uid)
        );
        
        const campaignsSnapshot = await getDocs(campaignsQuery);
        const campaigns = [];
        let totalReach = 0;
        let totalEngagement = 0;
        let engagementCount = 0;
        let totalSpent = 0;
        let activeCount = 0;
        let totalInfluencers = 0;
        
        // Get current date for status comparison
        const currentDate = new Date();
        
        campaignsSnapshot.forEach(doc => {
          const campaign = { id: doc.id, ...doc.data() };
          
          // Check if campaign has ended based on endDate
          const endDate = campaign.endDate ? new Date(campaign.endDate) : null;
          
          // Update campaign status based on dates
          if (endDate && currentDate > endDate) {
            campaign.status = 'completed';
          }
          
          campaigns.push(campaign);
          
          // Calculate overall stats
          if (campaign.status === 'active') {
            activeCount++;
          }
          
          // Use filledSpots for influencer count instead of influencers array length
          const campaignInfluencers = campaign.filledSpots || 0;
          totalInfluencers += campaignInfluencers;
          totalReach += campaign.metrics?.reach || 0;
          
          if (campaign.metrics?.engagement) {
            totalEngagement += parseFloat(campaign.metrics.engagement);
            engagementCount++;
          }
          
          totalSpent += parseFloat(campaign.budget || 0);
        });
        
        setCampaignStats(campaigns);
        setOverallStats({
          totalCampaigns: campaigns.length,
          activeCampaigns: activeCount,
          totalInfluencers,
          totalReach,
          averageEngagement: engagementCount > 0 ? (totalEngagement / engagementCount).toFixed(2) : 0,
          totalSpent
        });

        // Fetch all analytics data
        try {
          // 1. Basic tracking analytics (clicks)
          const trackingData = await trackingAnalyticsService.getBrandTrackingAnalytics(currentUser.uid);
          setTrackingAnalytics(trackingData);

          // 2. Content monitoring analytics (automatic content detection)
          const contentMonitoringPromises = campaigns.map(campaign => 
            contentMonitoringService.getContentDetectionAnalytics(campaign.id)
          );
          const contentMonitoringResults = await Promise.all(contentMonitoringPromises);
          
          const aggregatedContentData = contentMonitoringResults.reduce((acc, data) => ({
            totalDetections: acc.totalDetections + data.totalDetections,
            totalReach: acc.totalReach + data.totalReach,
            totalEngagement: acc.totalEngagement + data.totalEngagement,
            platformBreakdown: {
              ...acc.platformBreakdown,
              ...Object.keys(data.platformBreakdown).reduce((platforms, platform) => {
                platforms[platform] = (acc.platformBreakdown[platform] || 0) + data.platformBreakdown[platform];
                return platforms;
              }, {})
            },
            detections: [...acc.detections, ...data.detections]
          }), {
            totalDetections: 0,
            totalReach: 0,
            totalEngagement: 0,
            platformBreakdown: {},
            detections: []
          });
          
          setContentMonitoringData(aggregatedContentData);

          // 3. Embedded analytics (website/blog tracking)
          const embeddedAnalyticsPromises = campaigns.map(campaign => 
            embeddedAnalyticsService.getEmbeddedAnalytics(campaign.id)
          );
          const embeddedAnalyticsResults = await Promise.all(embeddedAnalyticsPromises);
          
          const aggregatedEmbeddedData = embeddedAnalyticsResults.reduce((acc, data) => ({
            totalEvents: acc.totalEvents + data.totalEvents,
            pageViews: acc.pageViews + data.pageViews,
            engagedViews: acc.engagedViews + data.engagedViews,
            deepEngagement: acc.deepEngagement + data.deepEngagement,
            qualityViews: acc.qualityViews + data.qualityViews,
            totalTimeOnPage: acc.totalTimeOnPage + data.totalTimeOnPage,
            averageTimeOnPage: 0, // Will calculate after aggregation
            eventsByType: {
              ...acc.eventsByType,
              ...Object.keys(data.eventsByType).reduce((types, type) => {
                types[type] = (acc.eventsByType[type] || 0) + data.eventsByType[type];
                return types;
              }, {})
            },
            eventsByInfluencer: {
              ...acc.eventsByInfluencer,
              ...Object.keys(data.eventsByInfluencer).reduce((influencers, influencer) => {
                influencers[influencer] = (acc.eventsByInfluencer[influencer] || 0) + data.eventsByInfluencer[influencer];
                return influencers;
              }, {})
            }
          }), {
            totalEvents: 0,
            pageViews: 0,
            engagedViews: 0,
            deepEngagement: 0,
            qualityViews: 0,
            totalTimeOnPage: 0,
            averageTimeOnPage: 0,
            eventsByType: {},
            eventsByInfluencer: {}
          });

          // Calculate average time on page
          if (aggregatedEmbeddedData.qualityViews > 0) {
            aggregatedEmbeddedData.averageTimeOnPage = Math.round(
              aggregatedEmbeddedData.totalTimeOnPage / aggregatedEmbeddedData.qualityViews
            );
          }
          
          setEmbeddedAnalyticsData(aggregatedEmbeddedData);
          
        } catch (analyticsError) {
          console.error('Error fetching analytics:', analyticsError);
          // Don't fail the whole page if analytics fail
        }
        
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Campaign Analytics</h2>
      
      <div className="space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Total Campaigns</h3>
            <p className="text-sm font-bold text-gray-900">{overallStats.totalCampaigns}</p>
            <p className="text-sm text-gray-600 mt-2">{overallStats.activeCampaigns} active campaigns</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Total Reach</h3>
            <p className="text-sm font-bold text-gray-900">{overallStats.totalReach.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-2">Across all campaigns</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Average Engagement</h3>
            <p className="text-sm font-bold text-gray-900">{overallStats.averageEngagement}%</p>
            <p className="text-sm text-gray-600 mt-2">Across all campaigns</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Total Influencers</h3>
            <p className="text-sm font-bold text-gray-900">{overallStats.totalInfluencers}</p>
            <p className="text-sm text-gray-600 mt-2">Across all campaigns</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Total Budget Spent</h3>
            <p className="text-sm font-bold text-gray-900">₱{overallStats.totalSpent.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-2">Across all campaigns</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Cost per Reach</h3>
            <p className="text-sm font-bold text-gray-900">
              ₱{overallStats.totalReach > 0 
                ? (overallStats.totalSpent / overallStats.totalReach).toFixed(2) 
                : '0.00'}
            </p>
            <p className="text-sm text-gray-600 mt-2">Average cost per person reached</p>
          </div>
        </div>

        {/* Campaign Selector */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Analytics Filter</h3>
            <Select
              value={selectedCampaign}
              onValueChange={(value) => setSelectedCampaign(value)}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="All Campaigns" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campaigns</SelectItem>
                {campaignStats.map(campaign => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name} ({campaign.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-gray-600">
            {selectedCampaign === 'all' 
              ? 'Showing aggregated data across all campaigns' 
              : `Showing data for: ${campaignStats.find(c => c.id === selectedCampaign)?.name || 'Selected Campaign'}`
            }
          </p>
        </div>

        {/* Advanced Analytics Suite */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Advanced Analytics Suite</h3>
            <p className="text-sm text-gray-600 mt-1">Comprehensive tracking and engagement analysis</p>
          </div>
          
          <div className="p-6">
            {/* Analytics Method Tabs */}
            <div className="mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button 
                    onClick={() => setActiveTab('social')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'social' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Social Media Analytics
                  </button>
                  <button 
                    onClick={() => setActiveTab('overview')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'overview' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Overview
                  </button>
                  <button 
                    onClick={() => setActiveTab('tracking')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'tracking' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Link Tracking
                  </button>
                  <button 
                    onClick={() => setActiveTab('content')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'content' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Content Detection
                  </button>
                  <button 
                    onClick={() => setActiveTab('website')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'website' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Website Analytics
                  </button>
                  <button 
                    onClick={() => setActiveTab('compare')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'compare' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <GitCompare className="h-4 w-4 inline mr-1" />
                    Compare Campaigns
                  </button>
                </nav>
              </div>
            </div>

            {/* Social Media Analytics Tab */}
            {activeTab === 'social' && (
              <SocialMediaAnalyticsDashboard 
                campaignData={campaignStats} 
                loading={loading} 
                selectedCampaign={selectedCampaign}
              />
            )}

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                {/* Overview Analytics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {/* Link Clicks */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-blue-800">Link Clicks</h4>
                        <p className="text-2xl font-bold text-blue-900">{trackingAnalytics.totalClicks.toLocaleString()}</p>
                        <p className="text-xs text-blue-600">Direct link engagement</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Detections */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-green-800">Content Detected</h4>
                        <p className="text-2xl font-bold text-green-900">{contentMonitoringData.totalDetections.toLocaleString()}</p>
                        <p className="text-xs text-green-600">Auto-discovered posts</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Website Events */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-purple-800">Page Views</h4>
                        <p className="text-2xl font-bold text-purple-900">{embeddedAnalyticsData.pageViews.toLocaleString()}</p>
                        <p className="text-xs text-purple-600">Website analytics</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Total Reach */}
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-orange-800">Total Reach</h4>
                        <p className="text-2xl font-bold text-orange-900">
                          {(overallStats.totalReach + contentMonitoringData.totalReach).toLocaleString()}
                        </p>
                        <p className="text-xs text-orange-600">Combined reach</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Link Tracking Tab */}
            {activeTab === 'tracking' && (
              <div className="space-y-6">
                {/* Link Tracking Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-blue-800">Total Clicks</h4>
                        <p className="text-2xl font-bold text-blue-900">
                          {selectedCampaign === 'all' 
                            ? (trackingAnalytics.totalClicks || 0).toLocaleString()
                            : (trackingAnalytics.campaignPerformance.find(c => c.campaignId === selectedCampaign)?.totalClicks || 0).toLocaleString()
                          }
                        </p>
                        <p className="text-xs text-blue-600">Tracking link clicks</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.1a3 3 0 105.656-5.656l-.1-1.1z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-green-800">Active Links</h4>
                        <p className="text-2xl font-bold text-green-900">
                          {selectedCampaign === 'all' 
                            ? (trackingAnalytics.totalTrackingLinks || 0).toLocaleString()
                            : campaignStats.filter(c => selectedCampaign === 'all' || c.id === selectedCampaign)
                                .reduce((sum, campaign) => sum + (campaign.filledSpots || 0), 0).toLocaleString()
                          }
                        </p>
                        <p className="text-xs text-green-600">Generated tracking links</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-purple-800">Avg Clicks/Link</h4>
                        <p className="text-2xl font-bold text-purple-900">
                          {(trackingAnalytics.totalTrackingLinks || 0) > 0 
                            ? ((trackingAnalytics.totalClicks || 0) / (trackingAnalytics.totalTrackingLinks || 1)).toFixed(1)
                            : '0'
                          }
                        </p>
                        <p className="text-xs text-purple-600">Average performance</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-orange-800">Mobile Clicks</h4>
                        <p className="text-2xl font-bold text-orange-900">
                          {((trackingAnalytics.clicksByPlatform && trackingAnalytics.clicksByPlatform.Mobile) || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-orange-600">
                          {(trackingAnalytics.totalClicks || 0) > 0 
                            ? `${(((trackingAnalytics.clicksByPlatform && trackingAnalytics.clicksByPlatform.Mobile) || 0) / (trackingAnalytics.totalClicks || 1) * 100).toFixed(1)}%`
                            : '0%'
                          } of total
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Platform Breakdown */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Platform Distribution</h4>
                  <div className="space-y-4">
                    {Object.entries((trackingAnalytics.clicksByPlatform || {})).map(([platform, clicks]) => (
                      <div key={platform} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            platform === 'Mobile' ? 'bg-blue-500' :
                            platform === 'Desktop' ? 'bg-green-500' :
                            'bg-purple-500'
                          }`}></div>
                          <span className="text-sm font-medium text-gray-700">{platform}</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-600">{(clicks || 0).toLocaleString()} clicks</span>
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                platform === 'Mobile' ? 'bg-blue-500' :
                                platform === 'Desktop' ? 'bg-green-500' :
                                'bg-purple-500'
                              }`}
                              style={{ 
                                width: `${(trackingAnalytics.totalClicks || 0) > 0 ? ((clicks || 0) / (trackingAnalytics.totalClicks || 1)) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-12">
                            {(trackingAnalytics.totalClicks || 0) > 0 
                              ? `${((clicks || 0) / (trackingAnalytics.totalClicks || 1) * 100).toFixed(1)}%`
                              : '0%'
                            }
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Performing Links */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900">Top Performing Links</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CTR</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Click</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(trackingAnalytics.topPerformingLinks || []).slice(0, 10).map((link, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {campaignStats.find(c => c.id === link.campaignId)?.name || 'Unknown Campaign'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {link.influencerName || 'Unknown Influencer'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(link.clicks || 0).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {link.ctr ? `${link.ctr.toFixed(2)}%` : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {link.lastClick ? new Date(link.lastClick).toLocaleDateString() : 'No clicks'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Content Detection Tab */}
            {activeTab === 'content' && (
              <div className="space-y-6">
                {/* Content Detection Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-green-800">Content Detected</h4>
                        <p className="text-2xl font-bold text-green-900">
                          {(contentMonitoringData.totalDetections || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-green-600">Auto-discovered posts</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-blue-800">Total Reach</h4>
                        <p className="text-2xl font-bold text-blue-900">
                          {(contentMonitoringData.totalReach || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-blue-600">From detected content</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-purple-800">Total Engagement</h4>
                        <p className="text-2xl font-bold text-purple-900">
                          {(contentMonitoringData.totalEngagement || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-purple-600">Likes, comments, shares</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-orange-800">Avg Engagement Rate</h4>
                        <p className="text-2xl font-bold text-orange-900">
                          {(contentMonitoringData.totalReach || 0) > 0 
                            ? (((contentMonitoringData.totalEngagement || 0) / (contentMonitoringData.totalReach || 1)) * 100).toFixed(2)
                            : '0'
                          }%
                        </p>
                        <p className="text-xs text-orange-600">From detected content</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Platform Breakdown */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Content by Platform</h4>
                  <div className="space-y-4">
                    {Object.entries((contentMonitoringData.platformBreakdown || {})).map(([platform, count]) => (
                      <div key={platform} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            platform === 'instagram' ? 'bg-pink-500' :
                            platform === 'facebook' ? 'bg-blue-500' :
                            platform === 'tiktok' ? 'bg-gray-800' :
                            'bg-purple-500'
                          }`}></div>
                          <span className="text-sm font-medium text-gray-700 capitalize">{platform}</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-600">{(count || 0).toLocaleString()} posts</span>
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                platform === 'instagram' ? 'bg-pink-500' :
                                platform === 'facebook' ? 'bg-blue-500' :
                                platform === 'tiktok' ? 'bg-gray-800' :
                                'bg-purple-500'
                              }`}
                              style={{ 
                                width: `${(contentMonitoringData.totalDetections || 0) > 0 ? ((count || 0) / (contentMonitoringData.totalDetections || 1)) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-12">
                            {(contentMonitoringData.totalDetections || 0) > 0 
                              ? `${((count || 0) / (contentMonitoringData.totalDetections || 1) * 100).toFixed(1)}%`
                              : '0%'
                            }
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Detections */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900">Recent Content Detections</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detected</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(contentMonitoringData.detections || []).slice(0, 10).map((detection, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`w-2 h-2 rounded-full mr-2 ${
                                  detection.platform === 'instagram' ? 'bg-pink-500' :
                                  detection.platform === 'facebook' ? 'bg-blue-500' :
                                  detection.platform === 'tiktok' ? 'bg-gray-800' :
                                  'bg-purple-500'
                                }`}></div>
                                <span className="text-sm font-medium text-gray-900 capitalize">
                                  {detection.platform}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {campaignStats.find(c => c.id === detection.campaignId)?.name || 'Unknown Campaign'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {detection.influencerName || 'Unknown Influencer'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(detection.reach || 0).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(detection.engagement || 0).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {detection.detectedAt ? new Date(detection.detectedAt).toLocaleDateString() : 'Unknown'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'website' && (
              <div className="text-center py-8 text-gray-500">
                Website analytics content coming soon...
              </div>
            )}

            {/* Compare Campaigns Tab */}
            {activeTab === 'compare' && (
              <div>
                {/* Campaign Selector */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Select up to 3 campaigns to compare</h4>
                  <div className="flex flex-wrap gap-2">
                    {campaignStats.map(campaign => {
                      const isSelected = compareSelectedIds.includes(campaign.id);
                      const isDisabled = !isSelected && compareSelectedIds.length >= 3;
                      return (
                        <button
                          key={campaign.id}
                          onClick={() => {
                            if (isSelected) {
                              setCompareSelectedIds(prev => prev.filter(id => id !== campaign.id));
                            } else if (compareSelectedIds.length < 3) {
                              setCompareSelectedIds(prev => [...prev, campaign.id]);
                            }
                          }}
                          disabled={isDisabled}
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            isSelected
                              ? 'bg-blue-600 text-white border-blue-600'
                              : isDisabled
                                ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {campaign.name}
                          <span className={`ml-2 text-xs ${isSelected ? 'text-blue-200' : 'text-gray-400'}`}>
                            ({campaign.status})
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {campaignStats.length === 0 && (
                    <p className="text-sm text-gray-500">No campaigns available to compare.</p>
                  )}
                </div>

                {/* Comparison Content */}
                {compareSelectedIds.length >= 2 ? (
                  <CampaignComparison campaigns={compareSelectedIds.map(id => campaignStats.find(c => c.id === id)).filter(Boolean)} />
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <GitCompare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Select at least 2 campaigns to see a side-by-side comparison.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Campaign Performance Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Campaign Performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link Clicks</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaignStats.map((campaign) => {
                  const campaignClicks = trackingAnalytics.campaignPerformance.find(
                    perf => perf.campaignId === campaign.id
                  )?.totalClicks || 0;
                  
                  return (
                    <tr key={campaign.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {campaign.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                          campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          campaign.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₱{parseFloat(campaign.budget || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(campaign.metrics?.reach || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {campaign.metrics?.engagement || 0}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {campaignClicks.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;

const CampaignComparison = ({ campaigns }) => {
  if (!campaigns || campaigns.length < 2) return null;

  // Helper to get metric value for a campaign
  const getMetric = (campaign, key) => {
    switch (key) {
      case 'status':
        return campaign.status;
      case 'budget':
        return parseFloat(campaign.budget || 0);
      case 'filledSpots':
        return campaign.filledSpots || 0;
      case 'reach':
        return campaign.metrics?.reach || 0;
      case 'engagement':
        return parseFloat(campaign.metrics?.engagement || 0);
      case 'clicks':
        return campaign.trackingAnalytics?.totalClicks || 0;
      case 'applications':
        return campaign.analytics?.applications || 0;
      case 'approvedApplications':
        return campaign.analytics?.approvedApplications || 0;
      case 'submittedContent':
        return campaign.analytics?.submittedContent || 0;
      case 'approvedContent':
        return campaign.analytics?.approvedContent || 0;
      case 'costPerReach': {
        const reach = campaign.metrics?.reach || 0;
        const budget = parseFloat(campaign.budget || 0);
        return reach > 0 ? budget / reach : null;
      }
      default:
        return 0;
    }
  };

  // Metrics configuration: key, label, formatter, higherIsBetter
  const metricsConfig = [
    { key: 'status', label: 'Status', format: (v) => v, higherIsBetter: null },
    { key: 'budget', label: 'Budget', format: (v) => formatCurrency(v), higherIsBetter: true },
    { key: 'filledSpots', label: 'Influencers', format: (v) => v.toLocaleString(), higherIsBetter: true },
    { key: 'reach', label: 'Reach', format: (v) => formatNumber(v), higherIsBetter: true },
    { key: 'engagement', label: 'Engagement Rate', format: (v) => `${v}%`, higherIsBetter: true },
    { key: 'clicks', label: 'Tracking Clicks', format: (v) => v.toLocaleString(), higherIsBetter: true },
    { key: 'applications', label: 'Applications', format: (v) => v.toLocaleString(), higherIsBetter: true },
    { key: 'approvedApplications', label: 'Approved', format: (v) => v.toLocaleString(), higherIsBetter: true },
    { key: 'submittedContent', label: 'Content Submitted', format: (v) => v.toLocaleString(), higherIsBetter: true },
    { key: 'approvedContent', label: 'Content Approved', format: (v) => v.toLocaleString(), higherIsBetter: true },
    { key: 'costPerReach', label: 'Cost per Reach', format: (v) => (v === null ? 'N/A' : formatCurrency(v.toFixed(2))), higherIsBetter: false },
  ];

  // Find best campaign index for each metric
  const getBestIndex = (key, higherIsBetter) => {
    if (higherIsBetter === null) return -1;
    const values = campaigns.map(c => getMetric(c, key));
    let bestIdx = 0;
    for (let i = 1; i < values.length; i++) {
      if (values[i] === null) continue;
      if (values[bestIdx] === null) {
        bestIdx = i;
        continue;
      }
      if (higherIsBetter && values[i] > values[bestIdx]) bestIdx = i;
      if (!higherIsBetter && values[i] < values[bestIdx]) bestIdx = i;
    }
    // Only highlight if there's a meaningful difference
    const bestVal = values[bestIdx];
    const hasDiff = values.some((v, i) => i !== bestIdx && v !== null && v !== bestVal);
    return hasDiff ? bestIdx : -1;
  };

  const statusBadgeClass = (status) => {
    if (status === 'active') return 'bg-green-100 text-green-800';
    if (status === 'completed') return 'bg-blue-100 text-blue-800';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Generate insights
  const generateInsights = () => {
    const insights = [];
    const validCampaigns = campaigns.filter(c => c);

    // Highest reach
    const reachSorted = [...validCampaigns].sort((a, b) => (b.metrics?.reach || 0) - (a.metrics?.reach || 0));
    if (reachSorted[0] && (reachSorted[0].metrics?.reach || 0) > 0) {
      insights.push({
        text: `🏆 ${reachSorted[0].name} has the highest reach at ${formatNumber(reachSorted[0].metrics?.reach || 0)}`,
      });
    }

    // Highest engagement
    const engagementSorted = [...validCampaigns].sort((a, b) => parseFloat(b.metrics?.engagement || 0) - parseFloat(a.metrics?.engagement || 0));
    if (engagementSorted[0] && parseFloat(engagementSorted[0].metrics?.engagement || 0) > 0) {
      insights.push({
        text: `🏆 ${engagementSorted[0].name} has the highest engagement rate at ${engagementSorted[0].metrics?.engagement}%`,
      });
    }

    // Best cost per reach (lowest)
    const cprSorted = [...validCampaigns]
      .filter(c => (c.metrics?.reach || 0) > 0)
      .sort((a, b) => {
        const aCpr = parseFloat(a.budget || 0) / (a.metrics?.reach || 1);
        const bCpr = parseFloat(b.budget || 0) / (b.metrics?.reach || 1);
        return aCpr - bCpr;
      });
    if (cprSorted[0]) {
      const cpr = parseFloat(cprSorted[0].budget || 0) / (cprSorted[0].metrics?.reach || 1);
      insights.push({
        text: `🏆 ${cprSorted[0].name} has the most efficient cost per reach at ₱${cpr.toFixed(2)}`,
      });
    }

    // Ratio insight for 2 campaigns
    if (validCampaigns.length === 2) {
      const [a, b] = reachSorted;
      const reachA = a.metrics?.reach || 0;
      const reachB = b.metrics?.reach || 0;
      if (reachA > 0 && reachB > 0 && reachA !== reachB) {
        const ratio = (reachA / reachB).toFixed(1);
        insights.push({
          text: `${a.name} has ${ratio}x the reach of ${b.name}`,
        });
      }
    }

    return insights;
  };

  const insights = generateInsights();
  const maxReach = Math.max(...campaigns.map(c => c.metrics?.reach || 0), 1);
  const barColors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500'];

  return (
    <div>
      {/* A. Comparison Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Metric</th>
              {campaigns.map((campaign) => (
                <th key={campaign.id} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {campaign.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {metricsConfig.map(({ key, label, format, higherIsBetter }) => {
              const bestIdx = getBestIndex(key, higherIsBetter);
              return (
                <tr key={key}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-700">{label}</td>
                  {campaigns.map((campaign, idx) => {
                    const value = getMetric(campaign, key);
                    const isBest = idx === bestIdx;
                    return (
                      <td key={campaign.id} className={`px-4 py-3 whitespace-nowrap text-sm text-gray-900 ${isBest ? 'bg-green-50' : ''}`}>
                        {key === 'status' ? (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadgeClass(value)}`}>
                            {value}
                          </span>
                        ) : (
                          format(value)
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* B. Reach Comparison Bar Chart */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Reach Comparison</h4>
        <div className="space-y-3">
          {campaigns.map((campaign, idx) => {
            const reach = campaign.metrics?.reach || 0;
            const percentage = (reach / maxReach) * 100;
            return (
              <div key={campaign.id}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{campaign.name}</span>
                  <span className="font-medium text-gray-900">{formatNumber(reach)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className={`h-3 rounded-full transition-all duration-500 ${barColors[idx]}`} style={{ width: `${percentage}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* C. Auto-generated Insights */}
      {insights.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {insights.map((insight, idx) => (
              <div key={idx} className="flex items-start bg-amber-50 border border-amber-200 rounded-lg p-4">
                <Lightbulb className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{insight.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
